import { useState } from "react";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import {
  Button,
  Card,
  CardContent,
  CardFooter,
  Input,
  Label,
} from "@pushpress/pushpress-ui";
import { useStripe } from "@stripe/react-stripe-js";
import { useMutation } from "@tanstack/react-query";
import { postSavePaymentMethod } from "../../../apis/billingApi";
import { useAnalytics } from "../../../components/providers/AnalyticsProvider/AnalyticsProvider";
import { extractError, defaultToastOptions } from "../../../utils/misc";
import { USER_UUID, CLIENT_UUID } from "../constants";
import SuccessScreen from "./SuccessScreen";
import { validEmailRegex } from "../helpers";

type PaymentComponentProps = {
  gymName: string;
  logoUrl: string;
  stripeClientSecret: string;
  stripeCustomer: string;
};

type FormValuesType = {
  name: string;
  email: string;
};

export default function AddCanadaBank({
  gymName,
  logoUrl,
  stripeClientSecret,
  stripeCustomer,
}: PaymentComponentProps) {
  const stripe = useStripe();
  const { trackEvent } = useAnalytics();
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<FormValuesType>({
    mode: "onTouched",
  });

  const userUuid = sessionStorage.getItem(USER_UUID);
  const clientUuid = sessionStorage.getItem(CLIENT_UUID);

  const { mutateAsync: savePaymentMethod } = useMutation(postSavePaymentMethod);

  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(
    sessionStorage.getItem(`payment_success_${userUuid}`) === "true",
  );

  const handleSavePaymentMethod = async (data: FormValuesType) => {
    setIsSaving(true);

    try {
      if (!stripe) {
        throw new Error("Stripe not found");
      }

      const { setupIntent, error } = await stripe.confirmAcssDebitSetup(
        stripeClientSecret,
        {
          payment_method: {
            billing_details: data,
          },
        },
      );

      if (error) {
        throw new Error(extractError(error));
      }

      if (!setupIntent) {
        throw new Error("There was an error saving your payment method");
      }

      const result = await savePaymentMethod({
        userUuid: userUuid,
        clientUuid,
        paymentMethodId: setupIntent.payment_method as string,
        customerId: stripeCustomer,
      });

      if (!result.wasSuccessful || !result.billing) {
        throw new Error("There was an error adding your payment method");
      } else {
        sessionStorage.setItem(`payment_success_${userUuid}`, "true");
        trackEvent("Add Payment Canada Bank Success", {
          user_uuid: userUuid,
          client_uuid: clientUuid,
        });
        setShowSuccessScreen(true);
      }
    } catch (error) {
      trackEvent("Add Payment Canada Bank Stripe Error", {
        error: "ap_payment_component_error",
        error_message: extractError(error),
      });
      toast.error(extractError(error), defaultToastOptions);
    } finally {
      setIsSaving(false);
    }
  };

  if (showSuccessScreen) {
    return <SuccessScreen logoUrl={logoUrl} gymName={gymName} />;
  }

  return (
    <div className="tw-min-h-screen tw-bg-blue-600 tw-flex tw-items-center tw-justify-center tw-p-4">
      <Card className="tw-w-full tw-max-w-lg tw-mx-auto">
        <CardContent className="tw-w-full tw-mt-6">
          <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-mb-6">
            {logoUrl && (
              <div className="tw-flex tw-items-center tw-justify-center tw-mb-4">
                <img
                  src={logoUrl}
                  width="50px"
                  className="tw-mb-1"
                  alt="gym logo"
                />
              </div>
            )}
            <span className="tw-text-md tw-text-gray-500">
              Please provide the bank account holder&apos;s name and email
            </span>
            <form
              noValidate
              onSubmit={handleSubmit(handleSavePaymentMethod)}
              className="tw-w-full"
            >
              <div className="tw-mt-4">
                <Label htmlFor="fullName">Full name</Label>
                <Input
                  placeholder="John Smith"
                  {...register("name", {
                    required: true,
                    minLength: 2,
                  })}
                  className={
                    errors.name
                      ? "tw-border-red-500 tw-focus-visible:tw-ring-red-500"
                      : ""
                  }
                />
                <div className="tw-h-4">
                  {errors.name && (
                    <div className="tw-text-xs tw-text-red-500">
                      Name is required
                    </div>
                  )}
                </div>
              </div>
              <div className="tw-mt-4">
                <Label htmlFor="email">Email</Label>
                <Input
                  type="email"
                  placeholder="john.smith@example.com"
                  {...register("email", {
                    required: true,
                    pattern: {
                      value: validEmailRegex,
                      message: "Invalid email address",
                    },
                  })}
                  className={
                    errors.email
                      ? "tw-border-red-500 tw-focus-visible:tw-ring-red-500"
                      : ""
                  }
                />
                <div className="tw-h-4">
                  {errors.email && (
                    <div className="tw-text-xs tw-text-red-500">
                      Email is required
                    </div>
                  )}
                </div>
              </div>
            </form>
          </div>
          <CardFooter className="tw-mt-6 tw-w-full tw-px-0 tw-py-0">
            <Button
              className="tw-w-full"
              onClick={handleSubmit(handleSavePaymentMethod)}
              disabled={!stripe || isSaving}
            >
              {isSaving ? (
                <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
                  <div className="tw-animate-spin tw-h-5 tw-w-5 tw-border-2 tw-border-white tw-border-t-transparent tw-rounded-full" />
                  Saving...
                </div>
              ) : (
                "Save"
              )}
            </Button>
          </CardFooter>
        </CardContent>
      </Card>
    </div>
  );
}
